import React from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Select from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { Slider } from './ui/slider';
import { supabase } from '../lib/supabaseClient';


const PRODUCT_TAGS = [
  { value: 'all', label: 'All Products' },
  { value: 'best_seller', label: '🏆 Best Sellers' },
  { value: 'new_release', label: '🆕 New Releases' },
  { value: 'prime', label: '✨ Prime' },
  { value: 'hot_deal', label: '🔥 Today\'s Deals' },
  { value: 'price_drop', label: '⚡ Price Drop' },
  { value: 'trending', label: '📈 Trending' },
  { value: 'most_wished', label: '⭐ Most Wished For' },
  { value: 'gift_idea', label: '🎁 Gift Ideas' }
];

async function fetchCategories() {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .eq('is_active', true)
    .order('display_order', { ascending: true });

  if (error) {
    console.error('Categories fetch error:', error);
    throw new Error(error.message);
  }

  return data || [];
}

export function Filters({ filters, onFilterChange }) {

  const { data: categories = [], isLoading, error } = useQuery({
    queryKey: ['categories'],
    queryFn: fetchCategories,
    staleTime: 300000,
  });

  const handlePriceChange = (values) => {
    onFilterChange({
      ...filters,
      minPrice: values[0],
      maxPrice: values[1]
    });
  };

  const handleCategoryChange = (value) => {
    onFilterChange({ ...filters, category: value });
  };

  const handleTagChange = (value) => {
    onFilterChange({ ...filters, productTags: value });
  };

  return (
    <div className="bg-white dark:bg-slate-800/90 rounded-xl shadow-lg dark:shadow-slate-900/50 p-6 space-y-6 border border-slate-200 dark:border-slate-700">
      {/* Search */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-white">Keywords</h3>
        <input
          type="text"
          placeholder="Search products..."
          className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg 
                     bg-white dark:bg-slate-700 
                     text-slate-900 dark:text-white 
                     placeholder:text-slate-400 dark:placeholder:text-slate-400
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent
                     transition-colors"
          value={filters.search}
          onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
        />
      </div>

      {/* Category Select */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-white">Category</h3>
        <Select.Root
          value={filters.category || 'all'}
          onValueChange={handleCategoryChange}
        >
          <Select.Trigger className="w-full flex items-center justify-between px-4 py-2.5 
                                      border border-slate-300 dark:border-slate-600 rounded-lg 
                                      bg-white dark:bg-slate-700 
                                      text-slate-900 dark:text-white
                                      hover:bg-slate-50 dark:hover:bg-slate-600
                                      transition-colors">
            <Select.Value placeholder="Select a category" />
            <Select.Icon>
              <ChevronDownIcon className="h-5 w-5 text-slate-400" />
            </Select.Icon>
          </Select.Trigger>

          <Select.Portal>
            <Select.Content className="bg-white dark:bg-slate-800 rounded-lg shadow-xl dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700 p-1 z-50">
              <Select.ScrollUpButton className="flex items-center justify-center h-6 text-slate-500 dark:text-slate-400">
                <ChevronUpIcon className="h-4 w-4" />
              </Select.ScrollUpButton>

              <Select.Viewport>
                <Select.Item
                  value="all"
                  className="relative flex items-center px-8 py-2 rounded-md 
                             text-slate-700 dark:text-slate-200
                             hover:bg-blue-50 dark:hover:bg-slate-700 
                             cursor-pointer outline-none
                             data-[highlighted]:bg-blue-50 dark:data-[highlighted]:bg-slate-700"
                >
                  <Select.ItemText>All Categories</Select.ItemText>
                  <Select.ItemIndicator className="absolute left-2 text-blue-600 dark:text-blue-400">
                    <CheckIcon className="h-4 w-4" />
                  </Select.ItemIndicator>
                </Select.Item>

                {isLoading ? (
                  <div className="px-8 py-2 text-slate-500 dark:text-slate-400 text-sm">
                    Loading categories...
                  </div>
                ) : error ? (
                  <div className="px-8 py-2 text-red-500 text-sm">
                    Error loading categories: {error.message}
                  </div>
                ) : categories.length > 0 ? (
                  categories.map((category) => (
                    <Select.Item
                      key={category.slug}
                      value={category.slug}
                      className="relative flex items-center px-8 py-2 rounded-md 
                                 text-slate-700 dark:text-slate-200
                                 hover:bg-blue-50 dark:hover:bg-slate-700 
                                 cursor-pointer outline-none
                                 data-[highlighted]:bg-blue-50 dark:data-[highlighted]:bg-slate-700"
                    >
                      <Select.ItemText>
                        {category.icon_name && (
                          <span className="mr-2" role="img" aria-label={category.name}>
                            {category.icon_name}
                          </span>
                        )}
                        {category.name}
                      </Select.ItemText>
                      <Select.ItemIndicator className="absolute left-2 text-blue-600 dark:text-blue-400">
                        <CheckIcon className="h-4 w-4" />
                      </Select.ItemIndicator>
                    </Select.Item>
                  ))
                ) : (
                  <div className="px-8 py-2 text-slate-500 dark:text-slate-400 text-sm">No categories found</div>
                )}
              </Select.Viewport>

              <Select.ScrollDownButton className="flex items-center justify-center h-6 text-slate-500 dark:text-slate-400">
                <ChevronDownIcon className="h-4 w-4" />
              </Select.ScrollDownButton>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </div>

      {/* Product Tags Select */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-white">Product Tags</h3>
        <Select.Root
          value={Array.isArray(filters.productTags) ? filters.productTags[0] : (filters.productTags || 'all')}
          onValueChange={(value) => {
            handleTagChange(value === 'all' ? 'all' : [value]);
          }}
        >
          <Select.Trigger className="w-full flex items-center justify-between px-4 py-2.5 
                                      border border-slate-300 dark:border-slate-600 rounded-lg 
                                      bg-white dark:bg-slate-700 
                                      text-slate-900 dark:text-white
                                      hover:bg-slate-50 dark:hover:bg-slate-600
                                      transition-colors">
            <Select.Value placeholder="Select a tag" />
            <Select.Icon>
              <ChevronDownIcon className="h-5 w-5 text-slate-400" />
            </Select.Icon>
          </Select.Trigger>

          <Select.Portal>
            <Select.Content className="bg-white dark:bg-slate-800 rounded-lg shadow-xl dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700 p-1 z-50">
              <Select.ScrollUpButton className="flex items-center justify-center h-6 text-slate-500 dark:text-slate-400">
                <ChevronUpIcon className="h-4 w-4" />
              </Select.ScrollUpButton>

              <Select.Viewport>
                {PRODUCT_TAGS.map(({ value, label }) => (
                  <Select.Item
                    key={value}
                    value={value}
                    className="relative flex items-center px-8 py-2 rounded-md 
                               text-slate-700 dark:text-slate-200
                               hover:bg-blue-50 dark:hover:bg-slate-700 
                               cursor-pointer outline-none
                               data-[highlighted]:bg-blue-50 dark:data-[highlighted]:bg-slate-700"
                  >
                    <Select.ItemText>{label}</Select.ItemText>
                    <Select.ItemIndicator className="absolute left-2 text-blue-600 dark:text-blue-400">
                      <CheckIcon className="h-4 w-4" />
                    </Select.ItemIndicator>
                  </Select.Item>
                ))}
              </Select.Viewport>

              <Select.ScrollDownButton className="flex items-center justify-center h-6 text-slate-500 dark:text-slate-400">
                <ChevronDownIcon className="h-4 w-4" />
              </Select.ScrollDownButton>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </div>

      {/* Price Range */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-white">Price Range</h3>
        <div className="px-2">
          <Slider
            defaultValue={[0, 1000]}
            max={1000}
            step={10}
            value={[filters.minPrice || 0, filters.maxPrice || 1000]}
            onValueChange={handlePriceChange}
            className="my-6"
          />
          <div className="flex justify-between text-sm text-slate-600 dark:text-slate-300">
            <span className="font-medium">${filters.minPrice || 0}</span>
            <span className="font-medium">${filters.maxPrice || 1000}</span>
          </div>
        </div>
      </div>

      {/* Stock Status */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-white">Stock Status</h3>
        <select
          className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg 
                     bg-white dark:bg-slate-700 
                     text-slate-900 dark:text-white
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent
                     transition-colors"
          value={filters.stockStatus || ''}
          onChange={(e) => onFilterChange({ ...filters, stockStatus: e.target.value })}
        >
          <option value="">All</option>
          <option value="in_stock">In Stock</option>
          <option value="out_of_stock">Out of Stock</option>
        </select>
      </div>

      {/* Sort By */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-white">Sort By</h3>
        <select
          className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg 
                     bg-white dark:bg-slate-700 
                     text-slate-900 dark:text-white
                     focus:ring-2 focus:ring-blue-500 focus:border-transparent
                     transition-colors"
          value={filters.sort || 'featured'}
          onChange={(e) => onFilterChange({ ...filters, sort: e.target.value })}
        >
          <option value="featured">Featured</option>
          <option value="price_asc">Price: Low to High</option>
          <option value="price_desc">Price: High to Low</option>
          <option value="rating_desc">Highest Rated</option>
          <option value="discount_desc">Biggest Discount</option>
        </select>
      </div>
    </div>
  );
}
